import React, { useEffect, useMemo, useRef, useState } from 'react'
import { PlusIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { Button, Heading, Input, LoadingSpinner, Text } from '../../components/ui'
import EstoqueItemCombobox from '../Estoque/EstoqueItemCombobox'
import { estoqueService } from '../../services/estoque'
import { marketingSolicitacaoService } from '../../services/marketingSolicitacao'
import { supplierQuotesService } from '../../services/supplierQuotes'
import type { EstoqueFornecedor, EstoqueItem, PaymentMethod } from '../../types/estoque'
import type {
  MarketingCampaign,
  MarketingEvent,
  MarketingInvoice,
  MarketingInvoiceDocumentType,
  MarketingInvoiceInput,
  MarketingInvoiceItemInput,
} from '../../types/marketingNotas'
import type { MarketingRequest } from '../../types/marketingSolicitacao'
import type { SupplierQuote } from '../../types/supplierQuotes'
import SolicitacaoPickerField from './SolicitacaoPickerField'
import PropostaPickerField from './PropostaPickerField'
import NotasPaymentStep from './NotasPaymentStep'
import { invoiceItemsFromQuote } from './propostasUi'
import {
  SELECT_CLASS,
  TEXTAREA_CLASS,
  documentTypeLabel,
  calcItemLineTotal,
  emptyDraftItem,
  emptyPaymentGroup,
  formatCurrency,
  formatDateOnlyPtBr,
  isConfirmCancelled,
  itemsFromRequestStock,
  paymentGroupsTotal,
  paymentsFromInvoiceRows,
  toPaymentGroupInputs,
  type PaymentDraftGroup,
} from './notasUi'

type Step = 'start' | 'form' | 'payment' | 'review'

type TimelineStep = {
  number: number
  label: string
}

type Props = {
  open: boolean
  editing: MarketingInvoice | null
  campaigns: MarketingCampaign[]
  events: MarketingEvent[]
  busy?: boolean
  onClose: () => void
  onSave: (input: MarketingInvoiceInput) => Promise<MarketingInvoice>
  onConclude: (invoice: MarketingInvoice) => Promise<void>
}

function InvoiceTimeline({
  steps,
  currentStep,
}: {
  steps: TimelineStep[]
  currentStep: number
}) {
  return (
    <ol className="flex items-start gap-0 overflow-x-auto pb-1">
      {steps.map((item, index) => {
        const isActive = item.number === currentStep
        const isDone = item.number < currentStep
        const isLast = index === steps.length - 1
        return (
          <li key={item.number} className="relative flex min-w-[9.5rem] flex-1 flex-col items-center px-1 text-center">
            <div className="relative mb-2 flex w-full items-center justify-center">
              {!isLast && (
                <span
                  className={`absolute left-1/2 top-1/2 z-0 h-0.5 w-full ${
                    isDone ? 'bg-primary-400' : 'bg-gray-200 dark:bg-gray-600'
                  }`}
                  aria-hidden
                />
              )}
              <span
                className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${
                  isActive
                    ? 'bg-primary-600 text-white ring-4 ring-primary-100 dark:ring-primary-900/40'
                    : isDone
                      ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/50 dark:text-primary-200'
                      : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300'
                }`}
              >
                {item.number}
              </span>
            </div>
            <p
              className={`text-sm font-medium leading-snug ${
                isActive
                  ? 'text-primary-700 dark:text-primary-300'
                  : isDone
                    ? 'text-gray-800 dark:text-gray-100'
                    : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              {item.label}
            </p>
          </li>
        )
      })}
    </ol>
  )
}

const NotasFormModal: React.FC<Props> = ({
  open,
  editing,
  campaigns,
  events,
  busy = false,
  onClose,
  onSave,
  onConclude,
}) => {
  const [step, setStep] = useState<Step>('start')
  const [documentType, setDocumentType] = useState<MarketingInvoiceDocumentType>('nfe')
  const [startRequestId, setStartRequestId] = useState('')
  const [pullItems, setPullItems] = useState(false)

  const [number, setNumber] = useState('')
  const [series, setSeries] = useState('')
  const [accessKey, setAccessKey] = useState('')
  const [freightValue, setFreightValue] = useState(0)
  const [discountValue, setDiscountValue] = useState(0)
  const [supplierId, setSupplierId] = useState('')
  const [issueDate, setIssueDate] = useState('')
  const [requestId, setRequestId] = useState('')
  const [supplierQuoteId, setSupplierQuoteId] = useState('')
  const [campaignId, setCampaignId] = useState('')
  const [eventId, setEventId] = useState('')
  const [notes, setNotes] = useState('')
  const [items, setItems] = useState<MarketingInvoiceItemInput[]>([emptyDraftItem('nfe')])
  const [paymentGroups, setPaymentGroups] = useState<PaymentDraftGroup[]>([
    emptyPaymentGroup(0),
  ])

  const [fornecedores, setFornecedores] = useState<EstoqueFornecedor[]>([])
  const [stockItems, setStockItems] = useState<EstoqueItem[]>([])
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([])
  const [requests, setRequests] = useState<MarketingRequest[]>([])
  const [quotes, setQuotes] = useState<SupplierQuote[]>([])
  const [loadingMeta, setLoadingMeta] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const suppressReinitRef = useRef(false)

  const editingId = editing?.id ?? null

  useEffect(() => {
    if (!open) {
      // Só libera o skip ao fechar o modal (sobrevive ao double-invoke do Strict Mode).
      suppressReinitRef.current = false
      return
    }
    // Após o 1º save na revisão, o parent promove editing null→id; não resetar o formulário.
    if (suppressReinitRef.current) return

    let cancelled = false
    const invoiceSnapshot = editing
    const load = async () => {
      setLoadingMeta(true)
      setError(null)
      try {
        const [fornecedoresData, stockData, requestsData, methodsData, quotesData] =
          await Promise.all([
            estoqueService.listFornecedores(false),
            estoqueService.listItens(false),
            marketingSolicitacaoService.listRequests(),
            estoqueService.listFormasPagamento(true),
            supplierQuotesService.list(),
          ])
        if (cancelled) return
        setFornecedores(fornecedoresData.filter((f) => f.is_active))
        setStockItems(stockData.filter((i) => i.is_active))
        setRequests(requestsData)
        setQuotes(
          quotesData.filter((q) => {
            if (q.status === 'descartada') return false
            if (q.status === 'nota_gerada' && q.id !== editing?.supplier_quote_id) return false
            return true
          })
        )

        if (invoiceSnapshot) {
          setStep('form')
          setDocumentType(invoiceSnapshot.document_type)
          setNumber(invoiceSnapshot.number || '')
          setSeries(invoiceSnapshot.series || '')
          setAccessKey(invoiceSnapshot.access_key || '')
          setFreightValue(Number(invoiceSnapshot.freight_value || 0))
          setDiscountValue(Number(invoiceSnapshot.discount_value || 0))
          setSupplierId(invoiceSnapshot.supplier_id || '')
          setIssueDate(invoiceSnapshot.issue_date?.slice(0, 10) || '')
          setRequestId(invoiceSnapshot.request_id || '')
          setSupplierQuoteId(invoiceSnapshot.supplier_quote_id || '')
          setCampaignId(invoiceSnapshot.campaign_id || '')
          setEventId(invoiceSnapshot.event_id || '')
          setNotes(invoiceSnapshot.notes || '')
          setItems(
            (invoiceSnapshot.items || []).map((item) => ({
              stock_item_id: item.stock_item_id,
              description: item.description,
              quantity: Number(item.quantity),
              unit_value: Number(item.unit_value),
              discount_value: Number(item.discount_value || 0),
              total_value: Number(item.total_value),
              sort_order: item.sort_order,
            }))
          )
          const loadedPayments = paymentsFromInvoiceRows(invoiceSnapshot.payments, methodsData)
          const usedMethodIds = new Set(
            (invoiceSnapshot.payments || []).map((p) => p.payment_method_id)
          )
          setPaymentMethods(
            methodsData.filter((m) => m.is_active || usedMethodIds.has(m.id))
          )
          const freight =
            invoiceSnapshot.document_type === 'nfe'
              ? Number(invoiceSnapshot.freight_value || 0)
              : 0
          const headerDiscount = Number(invoiceSnapshot.discount_value || 0)
          const itemsSum = (invoiceSnapshot.items || []).reduce(
            (acc, item) => acc + Number(item.total_value || 0),
            0
          )
          setPaymentGroups(
            loadedPayments.length > 0
              ? loadedPayments
              : [
                  emptyPaymentGroup(
                    itemsSum + freight - headerDiscount,
                    invoiceSnapshot.issue_date
                  ),
                ]
          )
        } else {
          setPaymentMethods(methodsData.filter((m) => m.is_active))
          setStep('start')
          setDocumentType('nfe')
          setStartRequestId('')
          setPullItems(false)
          setNumber('')
          setSeries('')
          setAccessKey('')
          setFreightValue(0)
          setDiscountValue(0)
          setSupplierId('')
          setIssueDate(new Date().toISOString().slice(0, 10))
          setRequestId('')
          setSupplierQuoteId('')
          setCampaignId('')
          setEventId('')
          setNotes('')
          setItems([emptyDraftItem('nfe')])
          setPaymentGroups([emptyPaymentGroup(0)])
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Erro ao carregar dados do formulário.')
        }
      } finally {
        if (!cancelled) setLoadingMeta(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
    // Só reinicializa ao abrir o modal ou trocar a nota (não após salvar rascunho).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- editing intencional via editingId
  }, [open, editingId])

  const activeCampaigns = useMemo(
    () => campaigns.filter((c) => c.is_active),
    [campaigns]
  )
  const activeEvents = useMemo(() => events.filter((e) => e.is_active), [events])

  const timelineSteps = useMemo<TimelineStep[]>(
    () => [
      { number: 1, label: 'Tipo e solicitação' },
      { number: 2, label: 'Dados e itens' },
      { number: 3, label: 'Forma de pagamento' },
      { number: 4, label: 'Revisão' },
    ],
    []
  )

  const currentTimelineStep =
    step === 'review'
      ? 4
      : step === 'start'
        ? 1
        : step === 'form'
          ? 2
          : 3

  const itemsTotal = useMemo(
    () =>
      items.reduce(
        (acc, item) =>
          acc +
          calcItemLineTotal(
            Number(item.quantity || 0),
            Number(item.unit_value || 0),
            Number(item.discount_value || 0)
          ),
        0
      ),
    [items]
  )
  const freightTotal = documentType === 'nfe' ? Number(freightValue || 0) : 0
  const headerDiscount = Number(discountValue || 0)
  const total =
    Math.round((itemsTotal + freightTotal - headerDiscount) * 10000) / 10000
  const paymentsAllocated = useMemo(() => paymentGroupsTotal(paymentGroups), [paymentGroups])
  const selectedQuote = useMemo(
    () => quotes.find((quote) => quote.id === supplierQuoteId) || null,
    [quotes, supplierQuoteId]
  )

  if (!open) return null

  const updateItem = (index: number, patch: Partial<MarketingInvoiceItemInput>) => {
    setItems((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item
        const next = { ...item, ...patch }
        const qty = Number(next.quantity || 0)
        const unit = Number(next.unit_value || 0)
        const discount = Number(next.discount_value || 0)
        next.total_value = calcItemLineTotal(qty, unit, discount)
        return next
      })
    )
  }

  const addItem = () => {
    setItems((prev) => [...prev, emptyDraftItem(documentType)])
  }

  const applySupplierQuote = (nextQuoteId: string) => {
    setSupplierQuoteId(nextQuoteId)
    if (!nextQuoteId) return
    const quote = quotes.find((row) => row.id === nextQuoteId)
    if (!quote) return
    setSupplierId(quote.supplier_id || '')
    setRequestId(quote.request_id || '')
    setFreightValue(Number(quote.freight_value || 0))
    setDiscountValue(Number(quote.discount_value || 0))
  }

  const pullItemsFromQuote = () => {
    if (!selectedQuote) return
    setItems(invoiceItemsFromQuote(selectedQuote))
  }

  const removeItem = (index: number) => {
    setItems((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)))
  }

  const goToFormFromStart = async () => {
    setError(null)
    let nextItems = [emptyDraftItem(documentType)]
    let nextRequestId = startRequestId || ''

    if (documentType === 'nfe' && startRequestId && pullItems) {
      try {
        const request = await marketingSolicitacaoService.getRequest(startRequestId)
        const pulled = itemsFromRequestStock(request?.items)
        if (pulled.length > 0) {
          nextItems = pulled
        } else {
          setError(
            'A solicitação não possui itens de brinde ou material impresso para puxar. Continue e inclua os itens manualmente.'
          )
          nextItems = [emptyDraftItem('nfe')]
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao carregar a solicitação.')
        return
      }
    }

    setRequestId(nextRequestId)
    setItems(nextItems)
    setStep('form')
  }

  const validateHeaderAndItems = (): string | null => {
    if (!supplierId) return 'Selecione o fornecedor.'
    if (!number.trim()) return 'Informe o número da nota.'
    if (!issueDate) return 'Informe a data de emissão.'
    if (items.length === 0) return 'Informe ao menos um item.'
    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      if (documentType === 'nfe' && !item.stock_item_id) {
        return `Selecione o item de estoque na linha ${i + 1}.`
      }
      if (documentType === 'nfse' && !String(item.description || '').trim()) {
        return `Informe a descrição do serviço na linha ${i + 1}.`
      }
      if (!Number.isFinite(Number(item.quantity)) || Number(item.quantity) <= 0) {
        return `Quantidade inválida na linha ${i + 1}.`
      }
      if (!Number.isFinite(Number(item.unit_value)) || Number(item.unit_value) < 0) {
        return `Valor unitário inválido na linha ${i + 1}.`
      }
      const itemDiscount = Number(item.discount_value || 0)
      if (!Number.isFinite(itemDiscount) || itemDiscount < 0) {
        return `Desconto inválido na linha ${i + 1}.`
      }
      const gross =
        Math.round(Number(item.quantity || 0) * Number(item.unit_value || 0) * 10000) / 10000
      if (itemDiscount > gross + 0.00005) {
        return `O desconto da linha ${i + 1} não pode ser maior que o total da linha.`
      }
    }
    if (Number(discountValue || 0) < 0) return 'Informe um desconto da nota válido.'
    if (headerDiscount > itemsTotal + freightTotal + 0.00005) {
      return 'O desconto da nota não pode ser maior que o total dos itens e frete.'
    }
    if (total < 0) return 'O total da nota não pode ser negativo.'
    return null
  }

  const goToPayment = () => {
    setError(null)
    const validationError = validateHeaderAndItems()
    if (validationError) {
      setError(validationError)
      return
    }
    const allocated = paymentGroupsTotal(paymentGroups)
    if (paymentGroups.length === 1 && allocated === 0) {
      setPaymentGroups([emptyPaymentGroup(total, issueDate)])
    }
    setStep('payment')
  }

  const validatePaymentsClient = (): string | null => {
    if (!paymentGroups.length) return 'Informe ao menos uma forma de pagamento.'
    for (let g = 0; g < paymentGroups.length; g++) {
      const group = paymentGroups[g]
      if (!group.payment_method_id) {
        return `Selecione a forma de pagamento no grupo ${g + 1}.`
      }
      if (!group.installments.length) {
        return `Informe ao menos uma parcela no grupo ${g + 1}.`
      }
      for (let i = 0; i < group.installments.length; i++) {
        const row = group.installments[i]
        if (!Number.isFinite(Number(row.amount)) || Number(row.amount) <= 0) {
          return `Valor inválido na parcela ${i + 1} do grupo ${g + 1}.`
        }
        if (!row.due_date) {
          return `Informe o vencimento da parcela ${i + 1} do grupo ${g + 1}.`
        }
      }
    }
    if (Math.abs(paymentsAllocated - total) > 0.009) {
      return `A soma dos pagamentos (${paymentsAllocated.toFixed(2)}) deve ser igual ao total da nota (${total.toFixed(2)}).`
    }
    return null
  }

  const goToReview = () => {
    setError(null)
    const headerError = validateHeaderAndItems()
    if (headerError) {
      setError(headerError)
      setStep('form')
      return
    }
    const paymentError = validatePaymentsClient()
    if (paymentError) {
      setError(paymentError)
      return
    }
    setStep('review')
  }

  const buildInput = (): MarketingInvoiceInput => ({
    document_type: documentType,
    number: number.trim(),
    series: series.trim() || null,
    access_key: accessKey.trim() || null,
    freight_value: documentType === 'nfe' ? Number(freightValue || 0) : 0,
    discount_value: Number(discountValue || 0),
    supplier_id: supplierId,
    issue_date: issueDate,
    request_id: requestId || null,
    supplier_quote_id: supplierQuoteId || null,
    campaign_id: campaignId || null,
    event_id: eventId || null,
    notes: notes.trim() || null,
    items: items.map((item, index) => ({
      ...item,
      sort_order: index * 10,
      quantity: Number(item.quantity),
      unit_value: Number(item.unit_value),
      discount_value: Number(item.discount_value || 0),
      total_value: calcItemLineTotal(
        Number(item.quantity || 0),
        Number(item.unit_value || 0),
        Number(item.discount_value || 0)
      ),
    })),
    payments: toPaymentGroupInputs(paymentGroups),
  })

  const persistDraft = async (): Promise<MarketingInvoice> => {
    suppressReinitRef.current = true
    return onSave(buildInput())
  }

  const handleSaveDraft = async () => {
    setError(null)
    try {
      await persistDraft()
      onClose()
    } catch (err) {
      suppressReinitRef.current = false
      setError(err instanceof Error ? err.message : 'Erro ao salvar a nota.')
    }
  }

  const handleConclude = async () => {
    setError(null)
    let persisted = false
    try {
      const saved = await persistDraft()
      persisted = true
      try {
        await onConclude(saved)
      } catch (err) {
        if (isConfirmCancelled(err)) {
          onClose()
          return
        }
        throw err
      }
      onClose()
    } catch (err) {
      if (!persisted) suppressReinitRef.current = false
      setError(err instanceof Error ? err.message : 'Erro ao concluir a nota.')
    }
  }

  const supplierName =
    fornecedores.find((f) => f.id === supplierId)?.name ||
    editing?.supplier?.name ||
    '—'
  const campaignName = campaigns.find((c) => c.id === campaignId)?.name || '—'
  const eventName = events.find((e) => e.id === eventId)?.name || '—'
  const requestLabel = (() => {
    const req = requests.find((r) => r.id === requestId)
    if (!req) return requestId ? 'Vinculada' : 'Não vinculada'
    return `#${req.request_number}`
  })()

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={busy ? undefined : onClose} />
      <div className="relative z-10 flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-800">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <div>
            <Heading as="h3" size="xl" className="text-gray-900 dark:text-white">
              {editing ? `Editar rascunho — ${documentTypeLabel(documentType)}` : 'Nova nota fiscal'}
            </Heading>
            {(step === 'form' || step === 'payment') && (
              <Text size="sm" color="muted" className="mt-1">
                Preencha os dados. Na revisão você pode salvar o rascunho ou concluir.
              </Text>
            )}
            {step === 'review' && (
              <Text size="sm" color="muted" className="mt-1">
                Revise os dados. Salve como rascunho ou conclua a nota
                {documentType === 'nfe' ? ' (entrada no estoque)' : ''}.
              </Text>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50 dark:hover:bg-gray-700"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-5">
          <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-4 dark:border-gray-700 dark:bg-gray-900/40">
            <InvoiceTimeline steps={timelineSteps} currentStep={currentTimelineStep} />
          </div>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
              {error}
            </div>
          )}

          {loadingMeta ? (
            <div className="flex justify-center py-16">
              <LoadingSpinner />
            </div>
          ) : step === 'start' ? (
            <div className="mx-auto max-w-lg space-y-4">
              <fieldset>
                <legend className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Tipo da nota *
                </legend>
                <div className="space-y-2">
                  <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 bg-white px-3 py-3 text-sm text-gray-800 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100">
                    <input
                      type="radio"
                      name="document_type"
                      className="mt-0.5"
                      checked={documentType === 'nfe'}
                      onChange={() => setDocumentType('nfe')}
                    />
                    <span>
                      <span className="font-medium">Nota fiscal eletrônica (NF-e)</span>
                      <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                        Produtos/itens com entrada no estoque ao confirmar
                      </span>
                    </span>
                  </label>
                  <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 bg-white px-3 py-3 text-sm text-gray-800 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100">
                    <input
                      type="radio"
                      name="document_type"
                      className="mt-0.5"
                      checked={documentType === 'nfse'}
                      onChange={() => {
                        setDocumentType('nfse')
                        setPullItems(false)
                      }}
                    />
                    <span>
                      <span className="font-medium">Nota fiscal de serviço (NFS-e)</span>
                      <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                        Serviços sem movimentação de estoque
                      </span>
                    </span>
                  </label>
                </div>
              </fieldset>

              <SolicitacaoPickerField
                label="Solicitação vinculada (opcional)"
                requests={requests}
                value={startRequestId}
                onChange={(id) => {
                  setStartRequestId(id)
                  if (!id) setPullItems(false)
                }}
              />

              {documentType === 'nfe' && startRequestId && (
                <label className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-200">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={pullItems}
                    onChange={(e) => setPullItems(e.target.checked)}
                  />
                  <span>
                    Puxar itens de brinde e material impresso da solicitação para a nota. O valor
                    unitário fica em branco para preenchimento.
                  </span>
                </label>
              )}

              {documentType === 'nfse' && (
                <Text size="sm" color="muted">
                  Nota de serviço não gera entrada em estoque. Os serviços são informados
                  manualmente nas linhas.
                </Text>
              )}
            </div>
          ) : step === 'review' ? (
            <div className="space-y-5">
              <Heading as="h4" size="lg" className="text-gray-900 dark:text-white">
                Revisão da nota
              </Heading>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Text size="xs" color="muted">
                    Tipo
                  </Text>
                  <Text size="sm" className="font-medium text-gray-900 dark:text-white">
                    {documentTypeLabel(documentType)}
                  </Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Número / Série
                  </Text>
                  <Text size="sm" className="font-medium text-gray-900 dark:text-white">
                    {number.trim() || '—'}
                    {series.trim() ? ` / ${series.trim()}` : ''}
                  </Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Fornecedor
                  </Text>
                  <Text size="sm" className="font-medium text-gray-900 dark:text-white">
                    {supplierName}
                  </Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Emissão
                  </Text>
                  <Text size="sm" className="font-medium text-gray-900 dark:text-white">
                    {issueDate ? formatDateOnlyPtBr(issueDate) : '—'}
                  </Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Solicitação
                  </Text>
                  <Text size="sm" className="font-medium text-gray-900 dark:text-white">
                    {requestLabel}
                  </Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Campanha
                  </Text>
                  <Text size="sm" className="font-medium text-gray-900 dark:text-white">
                    {campaignName}
                  </Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Evento
                  </Text>
                  <Text size="sm" className="font-medium text-gray-900 dark:text-white">
                    {eventName}
                  </Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Total
                  </Text>
                  <Text size="sm" className="font-medium text-gray-900 dark:text-white">
                    {formatCurrency(total)}
                  </Text>
                </div>
              </div>

              {notes.trim() && (
                <div>
                  <Text size="xs" color="muted">
                    Observações
                  </Text>
                  <Text size="sm" className="whitespace-pre-wrap text-gray-900 dark:text-white">
                    {notes.trim()}
                  </Text>
                </div>
              )}

              <div>
                <Text size="sm" className="mb-2 font-semibold text-gray-900 dark:text-white">
                  Itens · {formatCurrency(itemsTotal)}
                </Text>
                <ul className="space-y-2">
                  {items.map((item, index) => {
                    const lineTotal = calcItemLineTotal(
                      Number(item.quantity || 0),
                      Number(item.unit_value || 0),
                      Number(item.discount_value || 0)
                    )
                    return (
                      <li
                        key={`item-review-${index}`}
                        className="rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-700"
                      >
                        <span className="font-medium text-gray-900 dark:text-white">
                          {item.description || 'Item'}
                        </span>
                        <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                          Qtd {Number(item.quantity) || 0} · Un.{' '}
                          {formatCurrency(Number(item.unit_value) || 0)} ·{' '}
                          {formatCurrency(lineTotal)}
                        </span>
                      </li>
                    )
                  })}
                </ul>
                {(freightTotal > 0 || headerDiscount > 0) && (
                  <div className="mt-2 space-y-0.5 text-xs text-gray-500 dark:text-gray-400">
                    {freightTotal > 0 && <p>Frete: {formatCurrency(freightTotal)}</p>}
                    {headerDiscount > 0 && (
                      <p>Desconto: −{formatCurrency(headerDiscount)}</p>
                    )}
                  </div>
                )}
              </div>

              <div>
                <Text size="sm" className="mb-2 font-semibold text-gray-900 dark:text-white">
                  Formas de pagamento
                </Text>
                <ul className="space-y-2">
                  {paymentGroups.map((group, gIndex) => {
                    const methodName =
                      paymentMethods.find((m) => m.id === group.payment_method_id)?.name ||
                      'Forma não selecionada'
                    const groupTotal = group.installments.reduce(
                      (acc, row) => acc + (Number(row.amount) || 0),
                      0
                    )
                    return (
                      <li
                        key={`pay-review-${gIndex}`}
                        className="rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-700"
                      >
                        <p className="font-medium text-gray-900 dark:text-white">
                          {methodName} · {formatCurrency(groupTotal)}
                        </p>
                        <ul className="mt-1 space-y-0.5 text-xs text-gray-500 dark:text-gray-400">
                          {group.installments.map((inst, iIndex) => (
                            <li key={`pay-inst-${gIndex}-${iIndex}`}>
                              {iIndex + 1}ª · {formatCurrency(Number(inst.amount) || 0)} ·{' '}
                              {inst.due_date ? formatDateOnlyPtBr(inst.due_date) : '—'}
                            </li>
                          ))}
                        </ul>
                      </li>
                    )
                  })}
                </ul>
              </div>
            </div>
          ) : step === 'payment' ? (
            <NotasPaymentStep
              groups={paymentGroups}
              methods={paymentMethods}
              invoiceTotal={total}
              issueDate={issueDate}
              onChange={setPaymentGroups}
            />
          ) : (
            <>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Tipo
                  </label>
                  <Input value={documentTypeLabel(documentType)} disabled />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Número *
                  </label>
                  <Input value={number} onChange={(e) => setNumber(e.target.value)} />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Série
                  </label>
                  <Input value={series} onChange={(e) => setSeries(e.target.value)} />
                </div>
                <div className="sm:col-span-2 lg:col-span-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Fornecedor *
                    </label>
                    <select
                      className={SELECT_CLASS + ' w-full'}
                      value={supplierId}
                      onChange={(e) => setSupplierId(e.target.value)}
                    >
                      <option value="">Selecione o fornecedor</option>
                      {fornecedores.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <SolicitacaoPickerField
                    label="Solicitação"
                    requests={requests}
                    value={requestId}
                    onChange={setRequestId}
                    disabled={Boolean(editing && editing.status !== 'draft')}
                  />
                </div>
                <div className="sm:col-span-2 lg:col-span-3 space-y-2">
                  <PropostaPickerField
                    label="Cotação"
                    quotes={quotes}
                    value={supplierQuoteId}
                    onChange={applySupplierQuote}
                    disabled={Boolean(editing && editing.status !== 'draft')}
                  />
                  {selectedQuote && (
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={pullItemsFromQuote}
                        disabled={Boolean(editing && editing.status !== 'draft')}
                      >
                        Trazer itens
                      </Button>
                      <Text size="sm" color="muted">
                        Substitui os itens atuais pelos itens da proposta selecionada.
                      </Text>
                    </div>
                  )}
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Data de emissão *
                  </label>
                  <Input
                    type="date"
                    value={issueDate}
                    onChange={(e) => setIssueDate(e.target.value)}
                  />
                </div>
                {documentType === 'nfe' && (
                  <div className="sm:col-span-2">
                    <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Chave de acesso
                    </label>
                    <Input value={accessKey} onChange={(e) => setAccessKey(e.target.value)} />
                  </div>
                )}
                <div
                  className={`sm:col-span-2 lg:col-span-3 grid grid-cols-1 gap-3 ${
                    documentType === 'nfe' ? 'lg:grid-cols-4' : 'lg:grid-cols-3'
                  }`}
                >
                  {documentType === 'nfe' && (
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Frete
                      </label>
                      <Input
                        type="number"
                        min={0}
                        step="any"
                        value={freightValue}
                        onChange={(e) =>
                          setFreightValue(e.target.value === '' ? 0 : Number(e.target.value))
                        }
                      />
                    </div>
                  )}
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Desconto
                    </label>
                    <Input
                      type="number"
                      min={0}
                      step="any"
                      value={discountValue}
                      onChange={(e) =>
                        setDiscountValue(e.target.value === '' ? 0 : Number(e.target.value))
                      }
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Campanha
                    </label>
                    <select
                      className={SELECT_CLASS + ' w-full'}
                      value={campaignId}
                      onChange={(e) => setCampaignId(e.target.value)}
                    >
                      <option value="">Nenhuma</option>
                      {activeCampaigns.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Evento
                    </label>
                    <select
                      className={SELECT_CLASS + ' w-full'}
                      value={eventId}
                      onChange={(e) => setEventId(e.target.value)}
                    >
                      <option value="">Nenhum</option>
                      {activeEvents.map((ev) => (
                        <option key={ev.id} value={ev.id}>
                          {ev.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="sm:col-span-2 lg:col-span-3">
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Observações
                  </label>
                  <textarea
                    rows={2}
                    className={TEXTAREA_CLASS}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between gap-2 border-t border-gray-200 pt-4 dark:border-gray-700">
                <Heading as="h4" size="xl" className="text-gray-900 dark:text-white">
                  {documentType === 'nfe' ? 'Itens' : 'Serviços'}
                </Heading>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addItem}
                  className="inline-flex items-center gap-1"
                >
                  <PlusIcon className="h-4 w-4" />
                  Adicionar linha
                </Button>
              </div>

              <div className="space-y-3">
                {items.map((item, index) => (
                  <div
                    key={index}
                    className="rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/40"
                  >
                    <div className="mb-2 flex items-center justify-between">
                      <Text size="sm" className="font-medium text-gray-700 dark:text-gray-200">
                        Linha {index + 1}
                      </Text>
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        disabled={items.length <= 1}
                        className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-40 dark:hover:bg-red-900/20"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
                      {documentType === 'nfe' ? (
                        <div className="sm:col-span-2 lg:col-span-5">
                          <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                            Item de estoque *
                          </label>
                          <EstoqueItemCombobox
                            items={stockItems}
                            value={item.stock_item_id || ''}
                            onChange={(itemId) => {
                              const selected = stockItems.find((s) => s.id === itemId)
                              updateItem(index, {
                                stock_item_id: itemId,
                                description: selected
                                  ? `${selected.code} — ${selected.name}`
                                  : item.description,
                              })
                            }}
                            placeholder="Buscar item por nome ou código..."
                          />
                        </div>
                      ) : (
                        <div className="sm:col-span-2 lg:col-span-5">
                          <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                            Descrição do serviço *
                          </label>
                          <Input
                            value={item.description || ''}
                            onChange={(e) => updateItem(index, { description: e.target.value })}
                          />
                        </div>
                      )}
                      <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                          Quantidade *
                        </label>
                        <Input
                          type="number"
                          min={0}
                          step="any"
                          value={item.quantity}
                          onChange={(e) =>
                            updateItem(index, {
                              quantity: e.target.value === '' ? 0 : Number(e.target.value),
                            })
                          }
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                          Valor unitário *
                        </label>
                        <Input
                          type="number"
                          min={0}
                          step="any"
                          value={item.unit_value}
                          onChange={(e) =>
                            updateItem(index, {
                              unit_value: e.target.value === '' ? 0 : Number(e.target.value),
                            })
                          }
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                          Desconto
                        </label>
                        <Input
                          type="number"
                          min={0}
                          step="any"
                          value={item.discount_value ?? 0}
                          onChange={(e) =>
                            updateItem(index, {
                              discount_value: e.target.value === '' ? 0 : Number(e.target.value),
                            })
                          }
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                          Total
                        </label>
                        <Input
                          value={formatCurrency(
                            calcItemLineTotal(
                              Number(item.quantity || 0),
                              Number(item.unit_value || 0),
                              Number(item.discount_value || 0)
                            )
                          )}
                          disabled
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-1 border-t border-gray-200 pt-3 text-right dark:border-gray-700">
                {(freightTotal > 0 || headerDiscount > 0) && (
                  <>
                    <Text size="sm" color="muted">
                      Itens: {formatCurrency(itemsTotal)}
                    </Text>
                    {documentType === 'nfe' && freightTotal > 0 && (
                      <Text size="sm" color="muted">
                        Frete: {formatCurrency(freightTotal)}
                      </Text>
                    )}
                    {headerDiscount > 0 && (
                      <Text size="sm" color="muted">
                        Desconto: −{formatCurrency(headerDiscount)}
                      </Text>
                    )}
                  </>
                )}
                <Text size="sm" className="font-semibold text-gray-900 dark:text-white">
                  Total da nota: {formatCurrency(total)}
                </Text>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-gray-200 px-5 py-4 dark:border-gray-700">
          <div>
            {step !== 'start' && !(step === 'form' && editing) && (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setError(null)
                  if (step === 'review') setStep('payment')
                  else if (step === 'payment') setStep('form')
                  else setStep('start')
                }}
                disabled={busy}
              >
                Voltar
              </Button>
            )}
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
              Cancelar
            </Button>
            {step === 'start' && (
              <Button
                type="button"
                variant="solid"
                onClick={() => void goToFormFromStart()}
                disabled={busy || loadingMeta}
              >
                Continuar
              </Button>
            )}
            {step === 'form' && (
              <Button
                type="button"
                variant="solid"
                onClick={goToPayment}
                disabled={busy || loadingMeta}
              >
                Continuar para pagamento
              </Button>
            )}
            {step === 'payment' && (
              <Button
                type="button"
                variant="solid"
                onClick={goToReview}
                disabled={busy || loadingMeta}
              >
                Continuar
              </Button>
            )}
            {step === 'review' && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  disabled={busy}
                  onClick={() => void handleSaveDraft()}
                >
                  {busy ? 'Salvando...' : 'Salvar nota'}
                </Button>
                <Button
                  type="button"
                  variant="solid"
                  disabled={busy}
                  onClick={() => void handleConclude()}
                >
                  {busy ? 'Concluindo...' : 'Concluir'}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default NotasFormModal
